import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Collapse,
  Switch,
  FormControlLabel,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Cookie as CookieIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'layerstock_cookie_consent';

export const CookieConsent = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  type CookieCategory = 'necessary' | 'analytics' | 'marketing' | 'preferences';

  type Preferences = Record<CookieCategory, boolean>;

  const [preferences, setPreferences] = useState<Preferences>({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(allAccepted));
    setIsVisible(false);
    // Here you would typically trigger your analytics/marketing scripts
    console.log('All cookies accepted:', allAccepted);
  };

  const handleAcceptSelected = () => {
    const selectedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(selectedPreferences));
    setIsVisible(false);
    // Here you would typically trigger only the accepted scripts
    console.log('Selected cookies accepted:', selectedPreferences);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(onlyNecessary));
    setIsVisible(false);
    // Here you would typically disable all non-necessary scripts
    console.log('Only necessary cookies accepted:', onlyNecessary);
  };

  const handlePreferenceChange = (category: CookieCategory) => {
    if (category === 'necessary') return; // Cannot be changed
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const cookieCategories = [
    {
      key: 'necessary',
      title: t('cookies_necessary_title', 'Necessary Cookies'),
      description: t('cookies_necessary_desc', 'These cookies are essential for the website to function properly. They enable basic features like page navigation and access to secure areas.'),
      required: true
    },
    {
      key: 'analytics',
      title: t('cookies_analytics_title', 'Analytics Cookies'),
      description: t('cookies_analytics_desc', 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.'),
      required: false
    },
    {
      key: 'marketing',
      title: t('cookies_marketing_title', 'Marketing Cookies'),
      description: t('cookies_marketing_desc', 'These cookies are used to deliver personalized advertisements and track the effectiveness of our marketing campaigns.'),
      required: false
    },
    {
      key: 'preferences',
      title: t('cookies_preferences_title', 'Preference Cookies'),
      description: t('cookies_preferences_desc', 'These cookies allow the website to remember your preferences and provide enhanced, personalized features.'),
      required: false
    }
  ];

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        p: isMobile ? 1 : 2
      }}
    >
      <Paper
        elevation={8}
        sx={{
          maxWidth: 'lg',
          mx: 'auto',
          p: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <CookieIcon 
            sx={{ 
              color: 'primary.main', 
              fontSize: 32,
              flexShrink: 0,
              mt: 0.5
            }} 
          />
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t('cookies_title', 'We use cookies')}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('cookies_description', 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.')}
            </Typography>

            <Collapse in={showSettings}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('cookies_customize', 'Customize your preferences')}
                </Typography>
                
                <Stack spacing={2}>
                  {cookieCategories.map((category) => (
                    <Box key={category.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                          {category.title}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={preferences[category.key as keyof typeof CookieConsent]}
                              onChange={() => handlePreferenceChange(category.key as CookieCategory)}
                              disabled={category.required}
                              size="small"
                            />
                          }
                          label=""
                          sx={{ m: 0 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {category.description}
                      </Typography>
                      {category.key !== cookieCategories[cookieCategories.length - 1].key && (
                        <Divider sx={{ mt: 1.5 }} />
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Collapse>

            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={1}
              sx={{ alignItems: isMobile ? 'stretch' : 'center' }}
            >
              <Button
                variant="contained"
                onClick={handleAcceptAll}
                size="small"
                sx={{ fontWeight: 500 }}
              >
                {t('cookies_accept_all', 'Accept All')}
              </Button>
              
              {showSettings && (
                <Button
                  variant="outlined"
                  onClick={handleAcceptSelected}
                  size="small"
                  sx={{ fontWeight: 500 }}
                >
                  {t('cookies_accept_selected', 'Accept Selected')}
                </Button>
              )}
              
              <Button
                variant="text"
                onClick={handleRejectAll}
                size="small"
                color="secondary"
                sx={{ fontWeight: 500 }}
              >
                {t('cookies_reject_all', 'Reject All')}
              </Button>
              
              <Button
                variant="text"
                onClick={() => setShowSettings(!showSettings)}
                size="small"
                startIcon={<SettingsIcon />}
                sx={{ fontWeight: 500 }}
              >
                {showSettings ? t('cookies_hide_settings', 'Hide Settings') : t('cookies_customize', 'Customize')}
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" fontWeight={'bold'} sx={{ mt: 2, display: 'block' }}>
              {t('cookies_learn_more', 'Learn more about our cookie policy in our')}{' '}
              <Link to={'/legal/cookie-policy'} target='_blank'>
              <Box component="span" sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}>
                {t('Cookie Policy', 'Cookie Policy')}
              </Box>
              </Link>
            </Typography>
          </Box>
          
          <IconButton
            onClick={() => setIsVisible(false)}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
}